import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { vendorService } from '../services/vendor.service';
import { CreateVendorRequest, UpdateVendorRequest, VendorQueryParams } from '../types/vendor.types';

export const useVendorsPage = (params: VendorQueryParams) => {
  return useQuery({
    queryKey: ['vendors', 'page', params],
    queryFn: () => vendorService.getVendors(params),
    placeholderData: keepPreviousData,
  });
};

export const useVendorSummary = () => {
  return useQuery({
    queryKey: ['vendors', 'summary'],
    queryFn: () => vendorService.getSummary(),
  });
};

// What has been bought from one vendor, with its latest purchase orders
export const useVendorPurchaseSummary = (vendorId: string | null) => {
  return useQuery({
    queryKey: ['vendors', 'purchase-summary', vendorId],
    queryFn: () => vendorService.getPurchaseSummary(vendorId as string),
    enabled: !!vendorId,
  });
};

// Every vendor list, the dropdown on purchase orders included, starts with "vendors"
const useInvalidateVendors = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['vendors'] });
};

export const useCreateVendor = () => {
  const invalidate = useInvalidateVendors();
  return useMutation({
    mutationFn: (data: CreateVendorRequest) => vendorService.createVendor(data),
    onSuccess: invalidate,
  });
};

export const useUpdateVendor = () => {
  const invalidate = useInvalidateVendors();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVendorRequest }) => vendorService.updateVendor(id, data),
    onSuccess: invalidate,
  });
};

export const useDeleteVendor = () => {
  const invalidate = useInvalidateVendors();
  return useMutation({
    mutationFn: (id: string) => vendorService.deleteVendor(id),
    onSuccess: invalidate,
  });
};
